import {
  PRODUCT_LABEL,
  cleanStopName,
  delayClass,
  escapeHtml,
  formatClock,
  formatDelay,
} from '../format';
import type { Stopover, TripDetail, Vehicle } from '../types';

export interface DetailsHandlers {
  onClose: () => void;
  onToggleFollow: () => void;
  onFitRoute: () => void;
  onRetryTrip: () => void;
}

interface PanelState {
  vehicle: Vehicle;
  trip: TripDetail | null;
  error: string | null;
  loading: boolean;
  following: boolean;
}

/** Index of the first stop the vehicle has not reached yet. */
function nextStopIndex(stopovers: Stopover[], vehicle: Vehicle): number {
  const now = Date.now();
  for (let i = 0; i < stopovers.length; i += 1) {
    const stop = stopovers[i];
    if (!stop) continue;
    const time = Date.parse(stop.departure ?? stop.arrival ?? stop.plannedDeparture ?? '');
    if (Number.isFinite(time) && time >= now) return i;
  }
  // No usable times: fall back to the stop name the live feed reports.
  if (vehicle.nextStop) {
    const byName = stopovers.findIndex((stop) => stop.name === vehicle.nextStop);
    if (byName >= 0) return byName;
  }
  return stopovers.length;
}

function stopTimes(stop: Stopover): string {
  const actual = stop.departure ?? stop.arrival;
  const planned = stop.plannedDeparture ?? stop.plannedArrival;
  const delay = stop.departureDelay ?? stop.arrivalDelay;

  if (!actual && !planned) return '<span style="opacity:.4">—</span>';
  const delayed = delay !== null && Math.abs(delay) >= 60 && planned && actual && planned !== actual;
  const primary = formatClock(actual ?? planned);
  const cls = delayClass(delay);
  return delayed
    ? `<span class="planned">${formatClock(planned)}</span><span class="${cls}">${primary}</span>`
    : `<span>${primary}</span>`;
}

export class DetailsPanel {
  private state: PanelState | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly handlers: DetailsHandlers,
  ) {
    root.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement).closest('[data-action]');
      if (!target) return;
      const action = target.getAttribute('data-action');
      if (action === 'close') this.handlers.onClose();
      if (action === 'follow') this.handlers.onToggleFollow();
      if (action === 'fit') this.handlers.onFitRoute();
      if (action === 'retry') this.handlers.onRetryTrip();
    });
  }

  get isOpen(): boolean {
    return this.state !== null;
  }

  get vehicleId(): string | null {
    return this.state?.vehicle.id ?? null;
  }

  open(vehicle: Vehicle, following: boolean): void {
    this.state = { vehicle, trip: null, error: null, loading: true, following };
    this.render();
  }

  setTrip(trip: TripDetail): void {
    if (!this.state) return;
    this.state = { ...this.state, trip, loading: false, error: null };
    this.render();
  }

  setError(message: string): void {
    if (!this.state) return;
    this.state = { ...this.state, error: message, loading: false };
    this.render();
  }

  /** Keeps the live values (delay, next stop) fresh while the panel is open. */
  updateVehicle(vehicle: Vehicle): void {
    if (!this.state || this.state.vehicle.id !== vehicle.id) return;
    this.state = { ...this.state, vehicle };
    this.render();
  }

  setFollowing(following: boolean): void {
    if (!this.state) return;
    this.state = { ...this.state, following };
    this.render();
  }

  close(): void {
    this.state = null;
    this.root.hidden = true;
    this.root.innerHTML = '';
  }

  private render(): void {
    const state = this.state;
    if (!state) return;
    const { vehicle, trip, loading, error, following } = state;
    const line = vehicle.line;
    const scrollTop = this.root.querySelector('.details-body')?.scrollTop ?? 0;

    const destination = trip?.destination ?? vehicle.direction ?? 'Unbekanntes Ziel';
    const subtitle = [PRODUCT_LABEL[line.product], line.operator ?? trip?.line.operator]
      .filter(Boolean)
      .join(' · ');

    const finalArrival = trip?.stopovers.at(-1);
    const remarks = trip?.remarks.filter((text) => text.length > 3) ?? [];

    // With a trip loaded the timeline is the better source for "next stop":
    // the live feed sometimes still reports a stop the vehicle has left.
    const upcoming = trip ? trip.stopovers[nextStopIndex(trip.stopovers, vehicle)] : undefined;
    const nextStopName = upcoming?.name ?? vehicle.nextStop;
    const nextStopTime = upcoming
      ? (upcoming.arrival ?? upcoming.departure ?? upcoming.plannedArrival)
      : vehicle.nextStopTime;

    this.root.innerHTML = `
      <div class="details-head">
        <span class="line-badge" style="background:${escapeHtml(line.bg)};color:${escapeHtml(line.fg)}">
          ${escapeHtml(line.name)}
        </span>
        <div class="details-title">
          <h2>${escapeHtml(cleanStopName(destination))}</h2>
          <p>${escapeHtml(subtitle)}</p>
        </div>
        <button type="button" class="details-close" data-action="close" aria-label="Schließen">&times;</button>
      </div>

      <div class="details-meta">
        <div class="meta-cell">
          <span>Verspätung</span>
          <strong class="${delayClass(vehicle.delay)}">${escapeHtml(formatDelay(vehicle.delay))}</strong>
        </div>
        <div class="meta-cell">
          <span>Nächster Halt</span>
          <strong style="font-size:12.5px">${escapeHtml(
            nextStopName ? cleanStopName(nextStopName) : '—',
          )}</strong>
        </div>
        <div class="meta-cell">
          <span>${finalArrival ? 'Ankunft Ziel' : 'Abfahrt'}</span>
          <strong>${
            finalArrival
              ? formatClock(finalArrival.arrival ?? finalArrival.plannedArrival)
              : formatClock(nextStopTime)
          }</strong>
        </div>
      </div>
      ${
        vehicle.positionKind === 'estimated'
          ? `<p class="details-note">Position berechnet aus ${
              vehicle.scheduledOnly ? 'Fahrplanzeiten' : 'Echtzeitmeldungen'
            }, nicht gemessen.</p>`
          : ''
      }

      <div class="details-actions">
        <button type="button" class="icon-button" data-action="follow" aria-pressed="${following}">
          ${following ? 'Verfolgung beenden' : 'Fahrzeug verfolgen'}
        </button>
        <button type="button" class="icon-button" data-action="fit" ${trip ? '' : 'disabled'}>
          Ganze Route
        </button>
      </div>

      <div class="details-body">${this.renderBody(vehicle, trip, loading, error, remarks)}</div>
    `;

    this.root.hidden = false;
    const body = this.root.querySelector('.details-body');
    if (body) body.scrollTop = scrollTop;
  }

  private renderBody(
    vehicle: Vehicle,
    trip: TripDetail | null,
    loading: boolean,
    error: string | null,
    remarks: string[],
  ): string {
    if (loading) {
      return (
        '<div class="section-label">Fahrtverlauf</div>' +
        '<div class="skeleton" style="width:70%"></div>'.repeat(1) +
        '<div class="skeleton" style="width:85%"></div>' +
        '<div class="skeleton" style="width:60%"></div>' +
        '<div class="skeleton" style="width:78%"></div>'
      );
    }

    if (!trip) {
      const retry = error
        ? '<button type="button" class="icon-button note-retry" data-action="retry">Erneut versuchen</button>'
        : '';
      return `<div class="details-note">${escapeHtml(
        error ?? 'Für diese Fahrt liegt kein Fahrtverlauf vor.',
      )}${retry}</div>`;
    }

    const notes = remarks
      .map((text) => `<div class="details-note is-info">${escapeHtml(text)}</div>`)
      .join('');

    const next = nextStopIndex(trip.stopovers, vehicle);
    const rail = escapeHtml(trip.line.bg || vehicle.line.bg);

    const stops = trip.stopovers
      .map((stop, index) => {
        const classes = ['stop'];
        if (index < next) classes.push('is-passed');
        if (index === next) classes.push('is-next');
        if (stop.cancelled) classes.push('is-cancelled');
        const platform = stop.platform
          ? `<span class="stop-platform">Gl. ${escapeHtml(stop.platform)}</span>`
          : '<span></span>';
        return `
          <li class="${classes.join(' ')}" style="--rail-color:${rail}">
            <div class="stop-times">${stopTimes(stop)}</div>
            <div class="stop-rail"><span class="stop-node"></span></div>
            <div class="stop-name">${escapeHtml(cleanStopName(stop.name))}</div>
            ${platform}
          </li>`;
      })
      .join('');

    return `${notes}<div class="section-label">Fahrtverlauf · ${trip.stopovers.length} Halte</div>
      <ul class="timeline">${stops}</ul>`;
  }
}
